import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react'

import { flatten } from 'lodash'
import Fieldset from '../../Common/Components/Fieldset'
import FormRow from '../../Common/Components/FormRow'
import Heading from '../../Common/Components/Heading'
import { SelectOptions } from '../../Common/Components/Selects'

import { LanguageContext, ReactState } from '../../Common/Contexts'

import { ScopeFiltering as ScopeFilteringLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ComboBoxAlt } from '../../Common/Components/ComboBox/ComboBoxAlt'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { ALL_VALUE, ANY_VALUE_PARTITION_GUID } from '../../Common/Constants/DefaultValues'
import { RptFilterEntityInfo } from '../../Common/Types/Types'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { DeviceResponse } from './Types'
import { getDevices } from '../../../NonReact/common/API/EntityAPIClient'
import { DEVICE_CACHE_TIME } from './LocationalFiltering'
import { displayError } from '../../Common/Utils/Messages'
import { SCOPE_GROUPS } from '../../../NonReact/constants/DeviceConstants'
import { SelectOption } from '../../Common/Components/Selects/Types'
import { ALL_VALUE_STRING_DEFAULT } from '../../../NonReact/common/DefaultValues'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telSys: number
    handleTelSysChangeByUser: (newValue: number) => void
    partitions: string[]
    setPartitions: React.Dispatch<React.SetStateAction<string[]>>
    selectedPartitionsPartIDOnTelSys: Nullable<number[]>
    selectedScopeGroupsState: ReactState<string[]>
}

/**
 * Scope filtering section of Filter tab.
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telSys Report property.
 * @param handleTelSysChangeByUser Enhanced handler for telephone system change.
 * @param partitions Report property.
 * @param setPartitions Setter for corresponding report property.
 * @param selectedPartitionsPartIDOnTelSys Selected partitions IDs on the selected telephone system.
 * @param selectedScopeGroups Selected scope groups.
 * @param setSelectedScopeGroups Setter for selected scope groups.
 * @returns Scope filtering section of Filter tab.
 */
const ScopeFiltering: React.FC<Props> = ({
    filterEntityInfo,
    telSys,
    handleTelSysChangeByUser,
    partitions,
    setPartitions,
    selectedPartitionsPartIDOnTelSys,
    selectedScopeGroupsState: [selectedScopeGroups, setSelectedScopeGroups]
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ScopeFilteringLanguage.NAME)

    const DEFAULT_SCOPE_GROUPS_OPTION: SelectOption<string> = useMemo(
        (): SelectOption<string> => ({
            label: language.get(ScopeFilteringLanguage.AnyScopeGroupsLabel),
            value: ALL_VALUE_STRING_DEFAULT
        }),
        [language]
    )

    const [availableScopeGroups, setAvailableScopeGroups]: ReactState<SelectOptions<string>> = useState<
        SelectOptions<string>
    >([DEFAULT_SCOPE_GROUPS_OPTION])

    const telSysSelectOptions: SelectOptions<number> = filterEntityInfo
        ? filterEntityInfo.EntityInfo.TelSvrs.Data.map((telephoneSys) => ({
              value: parseInt(telephoneSys.ID, 10),
              label: telephoneSys.Description
          }))
        : []

    const partitionsSelectOptions: SelectOptions<string> = filterEntityInfo
        ? filterEntityInfo.EntityInfo.Partitions.Data.map((partition) => ({
              value: partition.ID,
              label: partition.Description
          }))
        : []

    /**
     * Handles partitions types select box changes. It deselects the '[Any]' item in case one of the normal was selected
     * and automatically selects '[Any]' if all items were unselected
     * @param newValue New selected value.
     */
    const handlePartitionsChange = (newValue: string[]): void => {
        setPartitions((prevValue: string[]) => {
            const prevValueHasAnyOptionSelected = prevValue.indexOf(ANY_VALUE_PARTITION_GUID) !== -1
            const newValueHasAnyOptionSelected = newValue.indexOf(ANY_VALUE_PARTITION_GUID) !== -1
            const anyOptionNewlySelected = !prevValueHasAnyOptionSelected && newValueHasAnyOptionSelected

            if (newValue.length === 0 || anyOptionNewlySelected) {
                return [ANY_VALUE_PARTITION_GUID]
            }
            return newValue.filter((value: string): boolean => value !== ANY_VALUE_PARTITION_GUID)
        })
    }

    useEffect((): void => {
        if (isVarNotEmpty(telSys) && telSys !== ALL_VALUE && !ArrayUtils.isEmpty(selectedPartitionsPartIDOnTelSys)) {
            Promise.all(
                selectedPartitionsPartIDOnTelSys
                    ?.map((partInTelSysID: Definable<number>): Definable<Promise<Voidable<DeviceResponse[]>>> => {
                        if (partInTelSysID === undefined) return undefined

                        return getDevices(
                            telSys,
                            partInTelSysID,
                            false,
                            SCOPE_GROUPS,
                            undefined,
                            DEVICE_CACHE_TIME,
                            undefined,
                            true
                        ).catch(displayError)
                    })
                    ?.filter(
                        (p: Definable<Promise<Voidable<DeviceResponse[]>>>): p is Promise<Voidable<DeviceResponse[]>> =>
                            p !== undefined
                    ) ?? []
            ).then((devicesArrays: Voidable<DeviceResponse[]>[]): void => {
                if (isVarNotEmpty(devicesArrays)) {
                    const allEnterpriseOrServiceProviderGroups: SelectOptions<string> = flatten(
                        devicesArrays.filter(isVarNotEmpty) as DeviceResponse[][]
                    ).map(
                        ({ DeviceNumber, Description }: DeviceResponse): SelectOption<string> => ({
                            label: Description,
                            value: DeviceNumber
                        })
                    )

                    setAvailableScopeGroups([DEFAULT_SCOPE_GROUPS_OPTION, ...allEnterpriseOrServiceProviderGroups])
                }
            })
        } else {
            setAvailableScopeGroups([DEFAULT_SCOPE_GROUPS_OPTION])
        }
    }, [telSys, JSON.stringify(selectedPartitionsPartIDOnTelSys)])

    return (
        <Fieldset className='scope-filtering'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(ScopeFilteringLanguage.ScopeFiltering_SectionHeader)}
            </Heading>
            <FormRow>
                <ComboBoxAlt
                    label={language.get(ScopeFilteringLanguage.TelephoneserverLabel)}
                    value={telSys}
                    handleChange={(newValue?: number | number[]): void => {
                        if (!IsVarEmpty(newValue) && !Array.isArray(newValue)) {
                            handleTelSysChangeByUser(newValue)
                        } else if (Array.isArray(newValue) && newValue.length > 0) {
                            handleTelSysChangeByUser(newValue[0])
                        }
                    }}
                    options={telSysSelectOptions}
                />
            </FormRow>
            <FormRow>
                <ComboBoxAlt
                    multiple
                    label={language.get(ScopeFilteringLanguage.PartitionsLabel)}
                    value={partitions}
                    handleChange={(newValue?: string | string[]): void => {
                        if (!IsVarEmpty(newValue) && Array.isArray(newValue)) {
                            handlePartitionsChange(newValue)
                        }
                    }}
                    options={partitionsSelectOptions}
                />
            </FormRow>
            <FormRow>
                <ComboBoxAlt
                    multiple
                    label={language.get(ScopeFilteringLanguage.ScopeGroupsLabel)}
                    value={selectedScopeGroups}
                    handleChange={(newValue?: string | string[]): void => {
                        if (Array.isArray(newValue)) {
                            const newValueFiltered: string[] = newValue.filter(
                                (value: string): boolean => value !== ALL_VALUE_STRING_DEFAULT
                            )

                            setSelectedScopeGroups(
                                newValueFiltered.length > 0 ? newValueFiltered : [ALL_VALUE_STRING_DEFAULT]
                            )
                        } else if (!Array.isArray(newValue)) {
                            if (isStringNotEmpty(newValue)) {
                                setSelectedScopeGroups([`${newValue}`])
                            } else {
                                setSelectedScopeGroups([ALL_VALUE_STRING_DEFAULT])
                            }
                        }
                    }}
                    options={availableScopeGroups}
                />
            </FormRow>
        </Fieldset>
    )
}

export default ScopeFiltering
